import React, { useRef, useState, useEffect } from 'react'
import { useTranslations } from 'next-intl'

import { MOBILE } from '@/src/constants/deviceTypes'
import { DeviceType } from '@/src/types/deviceType'

import PassportScan from './passportScan'

const MAX_IMAGE_SIZE = 5 * 1024 * 1024
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
const INSTRUCTION_KEYS = [
    { key: 'bioDataPage', ok: true },
    { key: 'fourCorners', ok: true },
    { key: 'goodLighting', ok: true },
    { key: 'noMrzCover', ok: false },
    { key: 'noGlare', ok: false },
] as const

interface PassportScanProps {
    onFileSelect: (file: File) => Promise<void>
    deviceType: DeviceType
}

async function compressImage(file: File): Promise<File | null> {
    return new Promise((resolve) => {
        const img = new Image()
        const url = URL.createObjectURL(file)

        img.onload = () => {
            URL.revokeObjectURL(url)

            const canvas = document.createElement('canvas')
            const MAX_DIM = 2048
            let { width, height } = img

            if (width > MAX_DIM || height > MAX_DIM) {
                const ratio = Math.min(MAX_DIM / width, MAX_DIM / height)
                width = Math.round(width * ratio)
                height = Math.round(height * ratio)
            }

            canvas.width = width
            canvas.height = height
            const ctx = canvas.getContext('2d')

            if (!ctx) {
                resolve(null)
                return
            }

            ctx.drawImage(img, 0, 0, width, height)
            let quality = 0.85

            const tryCompress = () => {
                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            resolve(null)
                            return
                        }

                        if (blob.size <= MAX_IMAGE_SIZE) {
                            resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }))
                        } else if (quality <= 0.3) {
                            resolve(null)
                        } else {
                            quality = parseFloat((quality - 0.1).toFixed(1))
                            tryCompress()
                        }
                    },
                    'image/jpeg',
                    quality,
                )
            }

            tryCompress()
        }

        img.onerror = () => {
            URL.revokeObjectURL(url)
            resolve(null)
        }
        img.src = url
    })
}

const PassportScanContainer: React.FC<PassportScanProps> = ({ onFileSelect, deviceType }) => {
    const t = useTranslations('passportScan')
    const uploadRef = useRef<HTMLInputElement>(null)
    const cameraRef = useRef<HTMLInputElement>(null)
    const isMobile = deviceType === MOBILE

    const [isLoading, setIsLoading] = useState(false)
    const [isCompressing, setIsCompressing] = useState(false)
    const [file, setFile] = useState<File | null>(null)
    const [preview, setPreview] = useState<string | null>(null)
    const [showNote, setShowNote] = useState(false)
    const [sizeError, setSizeError] = useState<string | null>(null)
    const [isInfoDrawerOpen, setIsInfoDrawerOpen] = useState(false)

    useEffect(() => {
        return () => {
            if (preview) {
                URL.revokeObjectURL(preview)
            }
        }
    }, [preview])

    const cleanupState = () => {
        if (preview) {
            URL.revokeObjectURL(preview)
        }
        setFile(null)
        setPreview(null)
        setShowNote(false)
        setSizeError(null)
        if (uploadRef.current) uploadRef.current.value = ''
        if (cameraRef.current) cameraRef.current.value = ''
    }

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const selected = event.target.files?.[0]
        if (!selected) return

        setSizeError(null)
        setShowNote(false)
        if (event.target) event.target.value = ''

        if (!ALLOWED_TYPES.includes(selected.type)) {
            setSizeError(t('invalidType'))
            return
        }

        let fileToUpload: File = selected

        if (selected.size > MAX_IMAGE_SIZE) {
            setIsCompressing(true)
            try {
                const compressed = await compressImage(selected)
                setIsCompressing(false)

                if (!compressed) {
                    setSizeError(t('tooLarge', { size: (selected.size / 1024 / 1024).toFixed(1) }))
                    return
                }
                fileToUpload = compressed
            } catch (error) {
                setIsCompressing(false)
                setSizeError(t('processFailed'))
                return
            }
        }

        const previewUrl = URL.createObjectURL(fileToUpload)
        if (preview) {
            URL.revokeObjectURL(preview)
        }

        setPreview(previewUrl)
        setFile(fileToUpload)

        setIsLoading(true)
        try {
            await onFileSelect(fileToUpload)
            setShowNote(true)
        } catch (error) {
            cleanupState()
        } finally {
            setIsLoading(false)
        }
    }

    const isBusy = isLoading || isCompressing
    const busyLabel = isCompressing ? t('compressingLabel') : t('readingLabel')
    const busySubLabel = isCompressing ? t('reducingSize') : t('pleaseWait')
    const busyColor = isCompressing ? 'secondary.main' : 'info.main'

    const instructionItems = INSTRUCTION_KEYS.map((item) => ({
        key: item.key,
        ok: item.ok,
        label: t(`instructions.${item.key}`)
    }))

    return (
        <PassportScan
            isMobile={isMobile}
            isBusy={isBusy}
            busyColor={busyColor}
            isInfoDrawerOpen={isInfoDrawerOpen}
            onInfoClick={() => {
                if (isMobile) {
                    setIsInfoDrawerOpen(true)
                }
            }}
            onCloseInfoDrawer={() => setIsInfoDrawerOpen(false)}
            onFileChange={handleFileChange}
            uploadRef={uploadRef}
            cameraRef={cameraRef}
            titleLabel={t('defaultLabel')}
            subtitleLabel={isBusy ? `${busyLabel} • ${busySubLabel}` : t('formatHint')}
            uploadLabel={t('upload')}
            cameraLabel={t('camera')}
            tooltipTitle={t('tooltipTitle')}
            instructionItems={instructionItems}
            sizeError={sizeError}
            showNote={showNote}
            reviewNote={t('reviewNote')}
        />
    )
}

export default PassportScanContainer
